import { useEffect } from 'react';
import { create } from 'zustand';
import type { PageRecord, Store } from '@nexus/store';

/**
 * In-memory catalogue of pages, aliases and tags, kept in step with the store.
 * Lets the editor's popups and link views answer synchronously as you type.
 */
interface CatalogState {
  pages: PageRecord[];
  byId: Map<string, PageRecord>;
  aliases: Map<string, string[]>;
  tags: string[];
}

export const useCatalog = create<CatalogState>(() => ({
  pages: [],
  byId: new Map(),
  aliases: new Map(),
  tags: [],
}));

export function useCatalogSync(store: Store, pages: PageRecord[] | undefined) {
  useEffect(() => {
    if (pages) useCatalog.setState({ pages, byId: new Map(pages.map((p) => [p.id, p])) });
  }, [pages]);

  useEffect(() => {
    const loadAliases = () =>
      void store.allAliases().then((aliases) => useCatalog.setState({ aliases }));
    const loadTags = () =>
      void store.listTags().then((rows) => useCatalog.setState({ tags: rows.map((r) => r.tag) }));
    loadAliases();
    loadTags();
    const offProps = store.subscribe(loadAliases, ['props']);
    const offIndex = store.subscribe(loadTags, ['index']);
    return () => {
      offProps();
      offIndex();
    };
  }, [store]);
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/** Rank pages for a query: exact > prefix > word prefix > substring; aliases count too. */
export function searchCatalog(query: string, limit = 20): PageRecord[] {
  const { pages, aliases } = useCatalog.getState();
  const q = norm(query.trim());
  if (!q) return [...pages].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, limit);
  const scored: { page: PageRecord; score: number }[] = [];
  for (const page of pages) {
    let best = 0;
    for (const name of [page.title, ...(aliases.get(page.id) ?? [])]) {
      const n = norm(name);
      const s =
        n === q
          ? 100
          : n.startsWith(q)
            ? 80
            : n.split(/[\s\-_/]+/).some((w) => w.startsWith(q))
              ? 60
              : n.includes(q)
                ? 40
                : 0;
      best = Math.max(best, s);
    }
    if (best) scored.push({ page, score: best });
  }
  return scored
    .sort((a, b) => b.score - a.score || b.page.updatedAt - a.page.updatedAt)
    .slice(0, limit)
    .map((s) => s.page);
}

export function findByName(name: string): PageRecord | null {
  const { pages, aliases } = useCatalog.getState();
  const n = norm(name.trim());
  return (
    pages.find((p) => norm(p.title) === n) ??
    pages.find((p) => (aliases.get(p.id) ?? []).some((a) => norm(a) === n)) ??
    null
  );
}
